import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { Button, buttonClassName } from "@/components/ui/Button";
import { CheckboxInput } from "@/components/ui/Checkbox";
import { Dialog } from "@/components/ui/Dialog";
import { Sheet } from "@/components/ui/Sheet";
import { ApiError, apiFetch } from "@/lib/api";
import type { TenancyReportColumnsResource } from "@/lib/api-types";
import { useIsPhone } from "@/lib/use-media-query";

/**
 * Owner-requested (2026-08-28): "a csv export that would include property
 * name, tenant name, phone, email, lease start and lease end... or better
 * an option to fields I want to export. The field options should be just
 * the details that show up on the cards, not all the details for tenant
 * and property." Mirrors `ColumnPickerDialog.tsx`'s own established shape
 * (fetch the real field list fresh every open, a plain `<a href>` download
 * carrying the selection as repeated `?column=` params — never `apiFetch`,
 * the same "a streamed file is never a good fetch response shape" reasoning
 * that file's own doc comment gives) — deliberately simpler than that
 * dialog, since this report has one flat field list, not several grouped
 * by entity.
 */
export function TenancyReportDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const isPhone = useIsPhone();
  const Container = isPhone ? Sheet : Dialog;

  const columnsQuery = useQuery({
    queryKey: ["tenancy-report-columns"],
    queryFn: () => apiFetch<TenancyReportColumnsResource>("/exports/tenancy-report/columns"),
    enabled: open,
  });

  const [selected, setSelected] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!open) {
      setSelected(new Set());
      return;
    }
    // Every field checked by default the first time the real list arrives
    // — an Owner asking for "an option to fields I want to export" is
    // choosing what to leave OUT, not building a selection from nothing.
    if (columnsQuery.data) {
      setSelected(new Set(columnsQuery.data.columns.map((column) => column.key)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, columnsQuery.data]);

  function toggleColumn(key: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }

  const columns = columnsQuery.data?.columns ?? [];
  const params = new URLSearchParams();
  for (const key of selected) {
    params.append("column", key);
  }
  const downloadHref = `/exports/tenancy-report.csv?${params.toString()}`;
  const canDownload = selected.size > 0;
  const errorMessage = columnsQuery.error instanceof ApiError ? columnsQuery.error.message : undefined;

  return (
    <Container open={open} onOpenChange={onOpenChange} title="Export tenancy report">
      <div className="mt-4 flex flex-col gap-4">
        <p className="text-body text-ink-secondary">
          One row per tenant, joined to their property, unit, and lease dates — pick which fields to include.
        </p>

        {columnsQuery.isError ? (
          <div className="rounded-lg border border-border-hairline bg-surface p-4 text-body text-ink">
            <p>Couldn&apos;t load the field list.</p>
            {errorMessage ? <p className="mt-1 text-ink-secondary">{errorMessage}</p> : null}
          </div>
        ) : columnsQuery.isPending ? (
          <p className="text-body text-ink-secondary">Loading fields…</p>
        ) : (
          <div className="flex max-h-[50vh] flex-col gap-2 overflow-y-auto pr-1">
            {columns.map((column) => (
              <label key={column.key} className="flex items-start gap-2 rounded-lg border border-border-hairline px-4 py-3">
                <CheckboxInput
                  className="mt-1"
                  checked={selected.has(column.key)}
                  onChange={() => toggleColumn(column.key)}
                />
                <span className="flex flex-col">
                  <span className="text-body text-ink">{column.label}</span>
                  <span className="text-meta text-ink-secondary">{column.description}</span>
                </span>
              </label>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between gap-2">
          <p className="text-meta text-ink-secondary">
            {selected.size} field{selected.size === 1 ? "" : "s"} selected
          </p>
          <div className="flex gap-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            {canDownload ? (
              <a href={downloadHref} className={buttonClassName("primary")} onClick={() => onOpenChange(false)}>
                Download CSV
              </a>
            ) : (
              <span className={buttonClassName("primary", "pointer-events-none opacity-50")} aria-disabled="true">
                Download CSV
              </span>
            )}
          </div>
        </div>
      </div>
    </Container>
  );
}
